import { Injectable } from '@angular/core';
import { clientPageTypeEnum } from '@core/enums';
import { SessionStorageService } from '@shared/services/storage';
import {
  getUrlPath,
  NavigationSourceParams,
  resolveNavigationSourceUrl,
} from '@modules/main/helpers/back-to-list-navigation.helper';
import { LIST_CONTEXT_STORAGE_KEY, parseListContextStore } from '@modules/main/helpers/list-context.helper';
import {
  NEIGHBOUR_LIST_SOURCE_STORAGE_KEY,
  NeighbourListSourceEntry,
  resolveNeighbourListSource,
} from '@modules/main/helpers/neighbour-list-source.helper';

/**
 * Pamięć strony wejścia dla nawigacji sąsiadów (Poprzedni/Następny) — osobna od
 * pamięci „Powrotu do listy" (`BackToListNavigationService`), bo obie mają
 * przeciwne wymagania wobec skoków podglądu-na-podgląd: powrót je resetuje
 * (celowo), a sąsiedzi muszą je przetrwać. Podpięta pod te same zdarzenia routera.
 */
@Injectable({providedIn: 'root'})
export class NeighbourListNavigationService {
  private pendingSourceUrl: string | null = null;

  constructor(private storageService: SessionStorageService) {
  }

  onNavigationStart(source: NavigationSourceParams): void {
    this.pendingSourceUrl = resolveNavigationSourceUrl(source);
  }

  onNavigationEnd(targetUrl: string, targetPageType: clientPageTypeEnum | null | undefined): void {
    const sourcePageUrl = this.pendingSourceUrl;
    this.pendingSourceUrl = null;

    const listContextStore = parseListContextStore(this.storageService.getItem(LIST_CONTEXT_STORAGE_KEY));
    const entry = resolveNeighbourListSource({
      sourcePageUrl,
      targetPageType,
      targetUrl,
      listContextStore,
      previousEntry: this.getStoredEntry(),
    });

    if (entry) {
      this.storageService.setItem(NEIGHBOUR_LIST_SOURCE_STORAGE_KEY, JSON.stringify(entry));
    } else {
      this.clearEntry();
    }
  }

  /** Wejście bezpośrednie albo odświeżenie — strony wejściowej nie da się odtworzyć. */
  onPageLoad(): void {
    this.clearEntry();
  }

  /** Adres listy będącej źródłem wejścia na bieżący podgląd, o ile wciąż aktualny. */
  getSourceUrl(currentUrl: string): string | null {
    const entry = this.getStoredEntry();

    if (!entry || getUrlPath(entry.previewUrl) !== getUrlPath(currentUrl)) {
      return null;
    }

    return entry.sourceUrl;
  }

  private getStoredEntry(): NeighbourListSourceEntry | null {
    const value = this.storageService.getItem(NEIGHBOUR_LIST_SOURCE_STORAGE_KEY);
    if (!value) {
      return null;
    }

    try {
      return JSON.parse(value) as NeighbourListSourceEntry;
    } catch {
      this.clearEntry();
      return null;
    }
  }

  private clearEntry(): void {
    this.storageService.removeItem(NEIGHBOUR_LIST_SOURCE_STORAGE_KEY);
  }
}
